import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { api, setAdminToken, authHeaders } from "@/lib/api";
import { Check, X, Trash2, Bookmark } from "lucide-react";

export default function AdminGuestsPage() {
  const { setAuthed } = useOutletContext();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [updatingId, setUpdatingId] = useState(null);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await api.get("/applications", { headers: authHeaders() });
      setApplications(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAdminToken(null);
        setAuthed(false);
        toast.error("Session expired. Enter passcode again.");
      } else {
        toast.error("Failed to load applications.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const res = await api.patch(
        `/applications/${id}/status`,
        { status },
        { headers: authHeaders() }
      );
      setApplications((prev) => prev.map((a) => (a.id === id ? res.data : a)));
      toast.success(
        status === "approved"
          ? "Approved."
          : status === "save_info"
          ? "Marked as potential invite."
          : "Declined."
      );
    } catch {
      toast.error("Update failed.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    setUpdatingId(id);
    try {
      await api.delete(`/applications/${id}`, { headers: authHeaders() });
      setApplications((prev) => prev.filter((a) => a.id !== id));
      toast.success("Application deleted.");
    } catch (err) {
      if (err?.response?.status === 401) {
        setAdminToken(null);
        setAuthed(false);
        toast.error("Session expired. Enter passcode again.");
      } else {
        toast.error("Delete failed.");
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = applications.filter((a) => {
    if (filter === "all") return true;
    if (filter === "vip") return a.status === "vip_confirmed";
    return a.status === filter;
  });

  const counts = {
    all: applications.length,
    pending: applications.filter((a) => a.status === "pending").length,
    approved: applications.filter((a) => a.status === "approved").length,
    declined: applications.filter((a) => a.status === "declined").length,
    save_info: applications.filter((a) => a.status === "save_info").length,
    vip: applications.filter((a) => a.status === "vip_confirmed").length,
  };

  return (
    <section className="container-kynn pt-14 pb-16">
      <div className="small-caps mb-6" style={{ color: "#B9929D" }}>
        Applications — sorted ascending by date
      </div>
      <h1
        className="font-display mb-10"
        style={{ fontSize: "clamp(48px, 6vw, 80px)", lineHeight: 1, color: "#F2EEE7", letterSpacing: "-0.02em" }}
      >
        RSVP <em style={{ color: "#DDBFCF" }}>List.</em>
      </h1>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="filter-chips">
        {[
          { k: "all", label: "All" },
          { k: "pending", label: "Pending" },
          { k: "approved", label: "Approved" },
          { k: "save_info", label: "Potential Invites" },
          { k: "declined", label: "Declined" },
          { k: "vip", label: "VIP — Instant" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`filter-${f.k}`}
            className="small-caps px-4 py-2 border transition-colors"
            style={{
              borderColor: filter === f.k ? "#B9929D" : "rgba(221, 191, 207,0.3)",
              background: filter === f.k ? "rgba(185, 146, 157,0.12)" : "transparent",
              color: filter === f.k ? "#B9929D" : "#F2EEE7",
              borderRadius: "6px",
            }}
          >
            {f.label} <span style={{ opacity: 0.5, marginLeft: 6 }}>{counts[f.k]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="italic" style={{ color: "#DDBFCF", opacity: 0.7 }}>Loading applications…</p>
      ) : filtered.length === 0 ? (
        <p className="italic" style={{ color: "#DDBFCF", opacity: 0.7 }}>
          No applications match this filter yet.
        </p>
      ) : (
        <div className="space-y-4" data-testid="applications-list">
          {filtered.map((a, i) => (
            <ApplicationCard
              key={a.id}
              app={a}
              index={i}
              onApprove={() => handleStatus(a.id, "approved")}
              onDecline={() => handleStatus(a.id, "declined")}
              onSaveInfo={() => handleStatus(a.id, "save_info")}
              onDelete={() => handleDelete(a.id)}
              busy={updatingId === a.id}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Pending review", bg: "rgba(221, 191, 207,0.12)", fg: "#DDBFCF", border: "rgba(221, 191, 207,0.4)" },
    approved: { label: "Approved", bg: "rgba(185, 146, 157,0.18)", fg: "#F2EEE7", border: "rgba(185, 146, 157,0.5)" },
    declined: { label: "Declined", bg: "rgba(140, 60, 60, 0.18)", fg: "#f0b0a8", border: "rgba(200,120,120,0.4)" },
    save_info: { label: "Potential invite", bg: "rgba(184, 201, 117, 0.15)", fg: "#d7e2a4", border: "rgba(184, 201, 117, 0.45)" },
    vip_confirmed: { label: "VIP — Instant", bg: "rgba(185, 146, 157,0.28)", fg: "#F2EEE7", border: "#B9929D" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className="small-caps px-3 py-1.5 border inline-block"
      style={{ background: s.bg, color: s.fg, borderColor: s.border, borderRadius: "6px" }}
    >
      {s.label}
    </span>
  );
}

function ApplicationCard({ app, index, onApprove, onDecline, onSaveInfo, onDelete, busy }) {
  const isVip = app.status === "vip_confirmed";
  const isPending = app.status === "pending";
  const createdAt = new Date(app.created_at);
  const dateStr = createdAt.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });

  return (
    <article
      data-testid={`application-card-${app.id}`}
      className="border p-6 sm:p-8 fade-up"
      style={{
        background: isVip ? "rgba(185, 146, 157,0.05)" : "rgba(48, 23, 40,0.55)",
        borderColor: isVip ? "rgba(185, 146, 157,0.35)" : "rgba(221, 191, 207,0.2)",
        animationDelay: `${index * 40}ms`,
        borderRadius: "8px",
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="small-caps mb-2" style={{ color: "#DDBFCF", opacity: 0.6 }}>
            #{String(index + 1).padStart(3, "0")} — {dateStr}
          </div>
          <h3
            className="font-display copy-field"
            style={{ fontSize: "28px", color: "#F2EEE7", lineHeight: 1.15, cursor: "copy" }}
            title="Click to copy name"
            data-testid={`copy-name-${app.id}`}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(app.full_name);
                toast.success("Copied name.");
              } catch {
                toast.error("Copy failed.");
              }
            }}
          >
            {app.full_name}
          </h3>
        </div>
        <StatusBadge status={app.status} />
      </div>

      {!isVip && (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 mt-6">
          <Field label="What they do" value={app.occupation} />
          <Field label="Socials" value={app.socials} />
          <Field label="City" value={app.city} />
          <Field label="Email" value={app.email} mono />
          <div className="md:col-span-2">
            <Field label="Currently excited about" value={app.working_on} />
          </div>
        </dl>
      )}

      {isVip && (
        <p className="italic mt-2" style={{ color: "#F2EEE7", opacity: 0.9 }}>
          Confirmed instantly via returning-guest code.
        </p>
      )}

      {isPending && (
        <div className="mt-8 pt-6 border-t flex items-center gap-3 flex-wrap"
             style={{ borderColor: "rgba(221, 191, 207,0.15)" }}>
          <button
            onClick={onApprove}
            disabled={busy}
            data-testid={`approve-btn-${app.id}`}
            className="btn-primary"
          >
            <Check size={14} /> Approve
          </button>
          <button
            onClick={onDecline}
            disabled={busy}
            data-testid={`decline-btn-${app.id}`}
            className="btn-ghost"
          >
            <X size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
            Decline
          </button>
          <button
            onClick={onSaveInfo}
            disabled={busy}
            data-testid={`save-info-btn-${app.id}`}
            className="small-caps px-4 py-2 transition-colors"
            style={{
              color: "#d7e2a4",
              background: "rgba(184, 201, 117, 0.10)",
              border: "1px solid rgba(184, 201, 117, 0.45)",
              borderRadius: "6px",
            }}
          >
            <Bookmark size={13} style={{ marginRight: 6, verticalAlign: "middle" }} />
            Potential Invite
          </button>
          <button
            onClick={onDelete}
            disabled={busy}
            data-testid={`delete-btn-${app.id}`}
            className="ml-auto small-caps px-3 py-2 transition-colors"
            style={{
              color: "#f0b0a8",
              opacity: 0.7,
              background: "transparent",
              border: "1px solid rgba(240,176,168,0.35)",
              borderRadius: "6px",
            }}
          >
            <Trash2 size={12} style={{ marginRight: 6, verticalAlign: "middle" }} />
            Delete
          </button>
        </div>
      )}

      {!isPending && (
        <div className="mt-6 pt-4 border-t flex justify-end"
             style={{ borderColor: "rgba(221, 191, 207,0.12)" }}>
          <button
            onClick={onDelete}
            disabled={busy}
            data-testid={`delete-btn-${app.id}`}
            className="small-caps px-3 py-2 transition-colors"
            style={{
              color: "#f0b0a8",
              opacity: 0.6,
              background: "transparent",
              border: "1px solid rgba(240,176,168,0.3)",
              borderRadius: "6px",
            }}
          >
            <Trash2 size={12} style={{ marginRight: 6, verticalAlign: "middle" }} />
            Delete
          </button>
        </div>
      )}
    </article>
  );
}

function Field({ label, value, mono }) {
  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`Copied ${label.toLowerCase()}.`);
    } catch {
      // Fallback for insecure contexts / older browsers
      const el = document.createElement("textarea");
      el.value = value;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
        toast.success(`Copied ${label.toLowerCase()}.`);
      } catch {
        toast.error("Copy failed.");
      }
      document.body.removeChild(el);
    }
  };

  return (
    <div>
      <div className="small-caps mb-1.5" style={{ color: "#B9929D" }}>
        {label}
      </div>
      <button
        type="button"
        onClick={handleCopy}
        data-testid={`copy-${label.replace(/\s+/g, "-").toLowerCase()}`}
        title="Click to copy"
        className={`copy-field text-left w-full ${mono ? "font-mono-body" : ""}`}
        style={{
          color: "#F2EEE7",
          opacity: 0.92,
          lineHeight: 1.6,
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "copy",
          font: "inherit",
        }}
      >
        {value}
      </button>
    </div>
  );
}
